import type {
  AdProvider,
  AdSize,
  AdSlotDefinition,
  MountedAdSlot,
} from '../types'
import { resolveTwoN4uAdRuntime, TWO_N4U_AD_TRANSPORT } from '../twoN4uRuntime'

/**
 * Narrow SDK typings for the 2n4uAd v0.3.1 / API 2.0 first-party Registry
 * profile. mioSegno has no Publisher credential: the browser uses only the
 * same-origin transport and the standard Nginx gateway resolves app identity.
 */
interface TwoN4uSlot {
  addService(service: TwoN4uPubAds): TwoN4uSlot
  defineSizeMapping(mapping: unknown): TwoN4uSlot
  setTargeting(key: string, value: string): TwoN4uSlot
  setCollapseEmptyDiv(collapse: boolean): TwoN4uSlot
}

interface TwoN4uSizeMappingBuilder {
  addSize(viewport: AdSize, sizes: readonly AdSize[]): TwoN4uSizeMappingBuilder
  build(): unknown
}

interface TwoN4uPubAds {
  setTargeting(key: string, value: string): TwoN4uPubAds
  setPrivacySettings(settings: {
    personalizedAds: false
    childDirected?: null
    underAgeOfConsent?: null
    limitedAds?: null
    restrictDataProcessing?: null
  }): TwoN4uPubAds
  refresh(slots?: TwoN4uSlot[]): void
  addEventListener?(eventName: string, listener: (event: unknown) => void): void
}

interface TwoN4uAdsSdk {
  cmd: Array<() => void>
  configure(config: {
    appId: string
    locale?: string
    privacy: { personalizedAds: false }
    transport: {
      readonly requestEndpoint: string
      readonly batchEndpoint: string
    }
  }): void
  defineSlot(adUnitId: string, sizes: readonly AdSize[], elementId: string): TwoN4uSlot
  sizeMapping(): TwoN4uSizeMappingBuilder
  pubads(): TwoN4uPubAds
  enableServices(): void
  display(elementId: string): void
  setLocale(locale: string): void
}

declare global {
  interface Window {
    twoN4uAds?: TwoN4uAdsSdk
  }
}

const APP_ID = 'mioSegno'
const SCRIPT_ID = 'miosegno-2n4u-ad-sdk'

function log(event: string, detail?: unknown) {
  // Detailed ad diagnostics are deliberately limited to the development build.
  if (!import.meta.env.DEV) return
  if (detail === undefined) console.debug(`[mioSegno][ads] ${event}`)
  else console.debug(`[mioSegno][ads] ${event}`, detail)
}

function currentRuntime() {
  return resolveTwoN4uAdRuntime(window.location.hostname, import.meta.env.DEV)
}

/**
 * Load the provider SDK and resolve only after its public methods exist.
 *
 * The pre-load `cmd` array is created exactly as the SDK contract describes.
 * Once the script's load event fires, mioSegno invokes the documented public
 * API directly instead of relying on undocumented post-load queue semantics.
 */
function ensureSdkScript(): Promise<TwoN4uAdsSdk> {
  if (window.twoN4uAds && typeof window.twoN4uAds.configure === 'function') {
    return Promise.resolve(window.twoN4uAds)
  }

  if (!window.twoN4uAds) {
    window.twoN4uAds = { cmd: [] } as unknown as TwoN4uAdsSdk
  }

  const runtime = currentRuntime()

  return new Promise((resolve, reject) => {
    const finishLoaded = () => {
      if (window.twoN4uAds && typeof window.twoN4uAds.configure === 'function') {
        log('SDK loaded', {
          src: runtime.sdkUrl,
          environment: runtime.environment,
          pageOrigin: window.location.origin,
        })
        resolve(window.twoN4uAds)
      } else {
        reject(new Error('2n4uAd SDK loaded without expected API'))
      }
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      if (window.twoN4uAds && typeof window.twoN4uAds.configure === 'function') {
        finishLoaded()
        return
      }
      existing.addEventListener('load', finishLoaded, { once: true })
      existing.addEventListener(
        'error',
        () => reject(new Error('2n4uAd SDK load failed')),
        { once: true },
      )
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.src = runtime.sdkUrl
    script.addEventListener('load', finishLoaded, { once: true })
    script.addEventListener(
      'error',
      () => reject(new Error('2n4uAd SDK load failed')),
      { once: true },
    )
    document.head.appendChild(script)
  })
}

export class TwoN4uAdProvider implements AdProvider {
  readonly providerId = '2n4uAd'

  private sdk: TwoN4uAdsSdk | null = null
  private appId = APP_ID
  private locale: string | undefined
  private configured = false
  private configurePromise: Promise<void> | null = null
  private servicesEnabled = false
  private lifecycleLoggingInstalled = false
  private mountedSlots = new Set<TwoN4uSlot>()

  /** Configure exactly once even when several React ad slots mount together. */
  async configure(input: { appId: string; locale?: string }): Promise<void> {
    this.appId = input.appId
    const requestedLocale = input.locale

    if (this.configured && this.sdk) {
      if (requestedLocale && requestedLocale !== this.locale) this.setLocale(requestedLocale)
      return
    }

    if (this.configurePromise) {
      await this.configurePromise
      if (requestedLocale && requestedLocale !== this.locale) this.setLocale(requestedLocale)
      return
    }

    this.locale = requestedLocale
    this.configurePromise = this.configureOnce()
    try {
      await this.configurePromise
    } finally {
      this.configurePromise = null
    }
  }

  private async configureOnce(): Promise<void> {
    const sdk = await ensureSdkScript()
    this.sdk = sdk
    const runtime = currentRuntime()

    sdk.configure({
      appId: this.appId,
      locale: this.locale,
      privacy: { personalizedAds: false },
      transport: TWO_N4U_AD_TRANSPORT,
    })

    const pubads = sdk.pubads()
    pubads
      .setPrivacySettings({
        personalizedAds: false,
        childDirected: null,
        underAgeOfConsent: null,
        limitedAds: null,
        restrictDataProcessing: null,
      })
      .setTargeting('environment', runtime.environment)

    this.installDevelopmentLifecycleLogging(pubads)
    this.configured = true
    log('provider configured', {
      appId: this.appId,
      locale: this.locale,
      environment: runtime.environment,
      pageOrigin: window.location.origin,
      requestEndpoint: TWO_N4U_AD_TRANSPORT.requestEndpoint,
    })
  }

  async mountSlot(definition: AdSlotDefinition): Promise<MountedAdSlot> {
    if (!this.configured || !this.sdk) {
      await this.configure({ appId: this.appId, locale: this.locale })
    }

    const sdk = this.sdk!

    // Commercial-ad lifecycle: define slot -> add service -> enable -> display.
    let slot = sdk.defineSlot(definition.adUnitId, definition.sizes, definition.elementId)

    if (definition.responsive?.length) {
      let mapping = sdk.sizeMapping()
      for (const rule of definition.responsive) {
        mapping = mapping.addSize(rule.viewport, rule.sizes)
      }
      slot = slot.defineSizeMapping(mapping.build())
    }

    for (const [key, value] of Object.entries(definition.targeting ?? {})) {
      slot = slot.setTargeting(key, value)
    }

    slot = slot
      .setCollapseEmptyDiv(definition.collapseWhenEmpty ?? true)
      .addService(sdk.pubads())

    this.mountedSlots.add(slot)

    if (!this.servicesEnabled) {
      sdk.enableServices()
      this.servicesEnabled = true
      log('services enabled')
    }

    log('display requested', {
      adUnitId: definition.adUnitId,
      elementId: definition.elementId,
      sizes: definition.sizes,
      targeting: definition.targeting,
      pageOrigin: window.location.origin,
    })
    sdk.display(definition.elementId)

    const mounted = slot
    return {
      elementId: definition.elementId,
      refresh: () => sdk.pubads().refresh([mounted]),
      destroy: () => {
        this.mountedSlots.delete(mounted)
        const element = document.getElementById(definition.elementId)
        if (element) element.replaceChildren()
      },
    }
  }

  setLocale(locale: string): void {
    if (locale === this.locale) return
    this.locale = locale
    if (!this.sdk || !this.configured) return

    // The app-selected locale is authoritative. Never infer from the browser.
    this.sdk.setLocale(locale)
    const slots = [...this.mountedSlots]
    if (slots.length > 0) this.sdk.pubads().refresh(slots)
    log('locale changed', { locale, refreshedSlots: slots.length })
  }

  private installDevelopmentLifecycleLogging(pubads: TwoN4uPubAds) {
    if (!import.meta.env.DEV || !pubads.addEventListener || this.lifecycleLoggingInstalled) return
    this.lifecycleLoggingInstalled = true

    const events = [
      'slotRequested',
      'slotResponseReceived',
      'slotRenderEnded',
      'slotOnload',
      'impressionViewable',
      'slotError',
    ]
    for (const eventName of events) {
      pubads.addEventListener(eventName, (event) => log(eventName, event))
    }
  }
}
